import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth.context";
import { useExperienceContext } from "@/context/experience.context";
import { functionsUrl } from "@/firebase/firebase";
import { useConversation } from "@11labs/react";
import axios from "axios";
import { FC, useEffect, useState, useRef } from "react";
import { Mic, MicOff, Volume2 } from "lucide-react";
import { cn } from "@/lib/utils";

const ElevenLabsChatButton: FC = () => {
  const { authUser } = useAuth();
  const { experience, realExperienceVirtualObjects } = useExperienceContext();

  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isConversationActive, setIsConversationActive] = useState(false);

  // Initialize conversation with Eleven Labs
  const conversation = useConversation({
    onConnect: () => {
      console.log("Eleven Labs conversation connected");
      setIsConversationActive(true);
    },
    onDisconnect: () => {
      console.log("Eleven Labs conversation disconnected");
      setIsConversationActive(false);
    },
    onError: (error) => {
      console.error("Eleven Labs conversation error:", error);
      setIsConversationActive(false);
    },
  });

  // Get the conversation status and speaking state
  const { status, isSpeaking } = conversation;

  // Get the signed URL from the Firebase function to use Eleven Labs API
  useEffect(() => {
    const getSignedUrl = async () => {
      try {
        //configure axios client with bearer token
        const token = await authUser.getIdToken();
        const client = axios.create({
          baseURL: `${functionsUrl}`,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        //get signed url from firebase function
        const apiResponse = await client.get(
          `brand/${experience.brandId}/eleven-labs/experience/${experience.id}/signed-url`
        );

        const response: { signedUrl: string } = apiResponse?.data;
        if (!response.signedUrl) throw new Error("Error getting signed url.");

        setSignedUrl(response.signedUrl);
      } catch (err) {
        console.error("Error in ElevenLabsChatButton:", err);
      }
    };

    // Check if the user is authenticated
    if (!authUser) return;

    getSignedUrl();
  }, [authUser, experience]);

  // If the signed URL is not available, return null to avoid rendering the button
  if (!signedUrl) return null;

  // Handle conversation start/stop
  const toggleConversation = async () => {
    try {
      if (status === "connected") {
        // End current conversation session
        await conversation.endSession();
        setIsConversationActive(false);
      } else {
        // Check for microphone permission before starting
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          // Start a new conversation session with the signed URL
          await conversation.startSession({ signedUrl: signedUrl });
          conversation.sendContextualUpdate(
            JSON.stringify({ shoes_on_the_wall: realExperienceVirtualObjects })
          );
          console.log(
            "realExperienceVirtualObjects",
            realExperienceVirtualObjects
          );
        } catch (err) {
          console.error("Microphone permission denied:", err);
          alert("Microphone access is required for the conversation feature.");
        }
      }
    } catch (err) {
      console.error("Error toggling conversation:", err);
    }
  };

  const getButtonIcon = () => {
    if (!isConversationActive)
      return (
        <>
          <Mic className="h-5 w-5 mr-2" /> <span>Speak with Charlie</span>
        </>
      );
    // if (isSpeaking) return <Volume2 className="h-5 w-5" />;
    return <MicOff className="h-5 w-5" />;
  };

  return (
    <div className="relative">
      <Button
        onClick={toggleConversation}
        variant={isConversationActive ? "destructive" : "default"}
        className={cn("relative", !isConversationActive && "animate-pulse")}
        size="default"
        aria-label={
          isConversationActive
            ? isSpeaking
              ? "AI is speaking"
              : "AI is listening"
            : "Talk to AI"
        }
      >
        {getButtonIcon()}
      </Button>

      {/* Show pulse effect when listening too */}
      {isConversationActive && !isSpeaking && (
        <div
          className={cn(
            "absolute inset-0 rounded-full opacity-20 pointer-events-none",
            "animate-pulse"
          )}
        />
      )}
    </div>
  );
};

export default ElevenLabsChatButton;
